import proj4 from 'proj4';
import { Extent } from './Extent';

export class Polygon {
  public coordinates: [number, number][][];
  public type: 'Polygon' = 'Polygon';
  private srid: string;

  constructor(coordinates: [number, number][][], srid: string = 'EPSG:4326') {
    this.coordinates = coordinates.map((ring) =>
      ring.map((c) => [...c] as [number, number])
    );
    this.srid = srid;
  }

  getCoordinates(): [number, number][][] {
    return this.coordinates.map((ring) =>
      ring.map((c) => [...c] as [number, number])
    );
  }

  setCoordinates(coords: [number, number][][]): void {
    this.coordinates = coords.map((ring) =>
      ring.map((c) => [...c] as [number, number])
    );
  }

  getArea(): number {
    const ring = this.coordinates[0];
    if (!ring || ring.length < 3) return 0;
    let area = 0;
    for (let i = 0; i < ring.length - 1; i++) {
      area += ring[i][0] * ring[i + 1][1];
      area -= ring[i + 1][0] * ring[i][1];
    }
    return Math.abs(area / 2);
  }

  getGeodesicArea(): number {
    const ring = this.coordinates[0];
    if (!ring || ring.length < 3) return 0;
    const toRad = (d: number) => (d * Math.PI) / 180;
    const R = 6371000;
    let area = 0;
    for (let i = 0; i < ring.length - 1; i++) {
      const p1 = ring[i];
      const p2 = ring[i + 1];
      area += toRad(p2[0] - p1[0]) * (2 + Math.sin(toRad(p1[1])) + Math.sin(toRad(p2[1])));
    }
    return Math.abs((area * R * R) / 2);
  }

  getExteriorRing(): [number, number][] {
    return this.coordinates[0]?.map((c) => [...c] as [number, number]) || [];
  }

  getInteriorRings(): [number, number][][] {
    return this.coordinates.slice(1).map((ring) =>
      ring.map((c) => [...c] as [number, number])
    );
  }

  getLinearRingCount(): number {
    return this.coordinates.length;
  }

  containsPoint(point: [number, number]): boolean {
    return this.containsCoordinate(point);
  }

  containsCoordinate(coord: [number, number]): boolean {
    const ring = this.coordinates[0];
    if (!ring) return false;
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const xi = ring[i][0], yi = ring[i][1];
      const xj = ring[j][0], yj = ring[j][1];
      if (
        yi > coord[1] !== yj > coord[1] &&
        coord[0] < ((xj - xi) * (coord[1] - yi)) / (yj - yi) + xi
      ) {
        inside = !inside;
      }
    }
    return inside;
  }

  getExtent(): Extent {
    const ring = this.coordinates[0] || [];
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const c of ring) {
      if (c[0] < minX) minX = c[0];
      if (c[1] < minY) minY = c[1];
      if (c[0] > maxX) maxX = c[0];
      if (c[1] > maxY) maxY = c[1];
    }
    return new Extent(minX, minY, maxX, maxY);
  }

  clone(): Polygon {
    return new Polygon(this.getCoordinates(), this.srid);
  }

  transform(fromProj: string, toProj: string): Polygon {
    const transformed = this.coordinates.map((ring) =>
      ring.map((c) => proj4(fromProj, toProj, [...c]) as [number, number])
    );
    return new Polygon(transformed, toProj);
  }

  toGeoJSON(): GeoJSON.Polygon {
    return {
      type: 'Polygon',
      coordinates: this.getCoordinates(),
    };
  }
}
